import React, { useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { findIcon } from "../../../../dashboard/icons";
import LineChart from "../../../../../components/chart/LineChart";
import { GRAPH_FILL_COLOR } from "../constant/graph-color-scheme";
import { checkProjectAccess } from "../utils/common";
import {
  formatLocalizedDate,
  transformGraphData,
  transformHeatMapData,
} from "../../../../../common/utils/utils";
import IndiaMap from "../../../../../components/chart/IndiaMap";
import DonutChart from "../../../../../components/chart/DonutChart";
import TreeMapChart from "../../../../../components/chart/TreeMapChart";
import CampaignCard from "../components/CampaignCard";
import useRedirect from "../../../../../components/custom-hooks/use-redirect";
import { useParams } from "react-router-dom";
import { filterOptions } from "../constant/filter-options";
import {
  useGetSummaryAnalytics,
  useGetSummaryList,
  useGetSummaryContentGraph,
  useGetSummaryContentRead,
  useGetSummaryCtaReport,
  useGetSummaryDeviceDistribution,
  useGetSummaryZonalDistribution,
  useGetLocationStateMap,
} from "../../../../../queries/query-hooks/workspace/sponsored-content-hook";
import MapAnalyticsSkeleton from "../../../../../skeleton/workspace/clirnet/brand-ads/map-analytics-skeleton";
import SquareChartSkeleton from "../../../../../skeleton/workspace/clirnet/brand-ads/square-chart-skeleton";
import { useGetProjectDetails } from "../../../../../queries/query-hooks/workspace/workspace-hook";
import FilterBy from "../components/FilterBy";
import ContentActionCard from "../components/ContentActionCard";
import MatrixSkeleton from "../../../../../skeleton/workspace/clirnet/sponsoredContent/MatrixSkeleton";
import dayjs from "dayjs";
import { ProjectHeaderTopBar, CardNotFound, SummeryMatrix } from "@ui";
import ProjectFilters from "../components/ProjectFilters";
import { useDispatch, useSelector } from "react-redux";
import { selectProject } from "store/project/project.selector";
import { updateProject } from "store/project/project.action";

function Summary() {
  const { project_id } = useParams();
  const { redirectTo } = useRedirect();
  const dispatch = useDispatch();
  const project = useSelector(selectProject);
  const [activeCampaignGroup, setActiveCampaignGroup] = useState({});
  const [activeSummaryTab, setActiveSummaryTab] = useState(null);
  const [contentIdsString, setContentIdsString] = useState("");
  const { data: projectDetails, isLoading: isProjectDetailsLoading } =
    useGetProjectDetails(project_id);
  const [dateRangePayload, setDateRangePayload] = useState({
    start_date: "",
    end_date: "",
  });
  const [isProjectChange, setIsProjectChange] = useState(false);

  useEffect(() => {
    setIsProjectChange(true);
  }, [project_id]);

  const { data: summaryList, isLoading: isSummaryListLoading } =
    useGetSummaryList(
      project_id,
      {
        start_date: dateRangePayload.start_date,
        end_date: dateRangePayload.end_date,
      },
      !!dateRangePayload.start_date && !!dateRangePayload.end_date
    );
  const contentIdList =
    summaryList && Array.isArray(summaryList) && summaryList.length > 0
      ? summaryList.map((item) => item?.content_id).join(",")
      : "";

  useEffect(() => {
    if (projectDetails && projectDetails?.length > 0) {
      const today = dayjs();
      const projectEndDate = dayjs(projectDetails[0]?.project_end_date);
      let result = projectDetails[0]?.project_end_date;

      if (
        projectEndDate &&
        (projectEndDate.isAfter(today) || projectEndDate.isSame(today, "day"))
      ) {
        result = today.subtract(1, "day");
      } else {
        result = projectEndDate;
      }

      if (project?.start_date && project?.end_date && !isProjectChange) {
        setDateRangePayload({
          start_date: project?.start_date,
          end_date: project?.end_date,
        });
        dispatch(
          updateProject({
            start_date: project?.start_date,
            end_date: project?.end_date,
          })
        );
      } else {
        setDateRangePayload({
          start_date: formatLocalizedDate(
            new Date(projectDetails[0]?.project_start_date),
            "YYYY-MM-DD[T]00:00:00.000Z"
          ),
          end_date: formatLocalizedDate(
            new Date(result),
            "YYYY-MM-DD[T]23:59:59.000Z"
          ),
        });
        dispatch(
          updateProject({
            start_date: formatLocalizedDate(
              new Date(projectDetails[0]?.project_start_date),
              "YYYY-MM-DD[T]00:00:00.000Z"
            ),
            end_date: formatLocalizedDate(
              new Date(result),
              "YYYY-MM-DD[T]23:59:59.000Z"
            ),
          })
        );
        setIsProjectChange(false);
      }
    }
  }, [projectDetails, project_id]);

  useEffect(() => {
    if (summaryList && summaryList?.length > 0) {
      const content_title =
        summaryList[0]?.fetch_with_image[0]?.content_title || "";
      const id = summaryList[0]?.content_id || 0;
      setActiveSummaryTab({ value: id, label: content_title });
      const contentList = summaryList.map((item) => {
        return item?.content_id || "";
      });
      setContentIdsString(contentList?.join(","));
    }
  }, [summaryList]);

  const [type, setType] = useState(filterOptions?.initial);

  const { data: summaryAnalytics, isLoading: isSummaryAnalyticsLoading } =
    useGetSummaryAnalytics(
      project_id,
      {
        start_date: dateRangePayload.start_date,
        end_date: dateRangePayload.end_date,
        content_id: contentIdsString || activeCampaignGroup?.id,
      },
      !!dateRangePayload.start_date &&
        !!dateRangePayload.end_date &&
        (!!contentIdsString || !!activeCampaignGroup?.id)
    );

  const { data: contentGraph, isLoading: isContentGraphLoading } =
    useGetSummaryContentGraph(
      project_id,
      {
        start_date: dateRangePayload.start_date,
        end_date: dateRangePayload.end_date,
        content_id: contentIdsString,
      },
      !!dateRangePayload.start_date &&
        !!dateRangePayload.end_date &&
        !!contentIdsString
    );

  const { data: contentReadGraphData, isLoading: isContentReadLoading } =
    useGetSummaryContentRead(
      project_id,
      {
        start_date: dateRangePayload.start_date,
        end_date: dateRangePayload.end_date,
        content_id: contentIdsString,
      },
      !!dateRangePayload.start_date &&
        !!dateRangePayload.end_date &&
        !!contentIdsString
    );

  const { data: summaryCtaReport, isLoading: isCtaReportLoading } =
    useGetSummaryCtaReport(
      project_id,
      {
        start_date: dateRangePayload.start_date,
        end_date: dateRangePayload.end_date,
        content_id: contentIdList,
      },
      !!dateRangePayload.start_date &&
        !!dateRangePayload.end_date &&
        !!contentIdList
    );

  const { data: stateMapData, isLoading: isStateMapLoading } =
    useGetLocationStateMap(
      project_id,
      type.toLowerCase(),
      {
        start_date: dateRangePayload.start_date,
        end_date: dateRangePayload.end_date,
        content_id: contentIdList,
        type: "percentage",
      },
      !!dateRangePayload.start_date &&
        !!dateRangePayload.end_date &&
        !!contentIdList
    );

  const {
    data: deviceDistributionData,
    isLoading: isDeviceDistributionLoading,
  } = useGetSummaryDeviceDistribution(
    project_id,
    type.toLowerCase(),
    {
      start_date: dateRangePayload.start_date,
      end_date: dateRangePayload.end_date,
      content_id: contentIdList,
    },
    !!dateRangePayload.start_date &&
      !!dateRangePayload.end_date &&
      !!contentIdList
  );

  const { data: zoneDistributionData, isLoading: isZonalDistributionLoading } =
    useGetSummaryZonalDistribution(
      project_id,
      type.toLowerCase(),
      {
        start_date: dateRangePayload.start_date,
        end_date: dateRangePayload.end_date,
        content_id: contentIdList,
      },
      !!dateRangePayload.start_date &&
        !!dateRangePayload.end_date &&
        !!contentIdList
    );

  const sponsoredContentList =
    summaryList && Array.isArray(summaryList) && summaryList.length > 0
      ? summaryList?.map((item) => {
          return {
            id: item?.content_id,
            group_title: item?.fetch_with_image[0]?.content_title,
          };
        })
      : [];

  const contentViewsChartOptions = {
    labels:
      contentGraph && contentGraph?.length > 0
        ? transformGraphData(contentGraph, "total_count", "formatted_date")
            ?.label
        : [],
    series:
      contentGraph && contentGraph?.length > 0
        ? transformGraphData(contentGraph, "total_count", "formatted_date")
            ?.series
        : [],
  };

  const contentReadChartOptions = {
    labels:
      contentReadGraphData && contentReadGraphData?.length > 0
        ? transformGraphData(
            contentReadGraphData,
            "read_count",
            "formatted_date"
          )?.label
        : [],
    series:
      contentReadGraphData && contentReadGraphData?.length > 0
        ? transformGraphData(
            contentReadGraphData,
            "read_count",
            "formatted_date"
          )?.series
        : [],
  };

  function transformBarChart(data) {
    if (data) {
      const series = [];
      const labels = [];

      for (const item of data) {
        const categoryName = item.cta_type_info?.at(0)?.name || "";
        const categoryData = item.cta_report.map(
          (report) => report.user_count || 0
        );
        series.push({ name: categoryName, data: categoryData });
        labels.push(...item.cta_report.map((report) => report.date));
      }
      const uniqueLabels = new Set(labels);
      return {
        series,
        labels: Array.from(uniqueLabels),
      };
    } else {
      return { series: [], labels: [] };
    }
  }

  const transformStateMapData = (data = [], key) => {
    return [
      data?.reduce((acc, item) => {
        if (item.name && item[key] !== null) {
          acc[item.name] = item[key];
        }
        return acc;
      }, {}),
    ];
  };

  const stateMapReadsData = transformStateMapData(
    stateMapData,
    "reads_percent"
  );
  const stateMapViewsData = transformStateMapData(
    stateMapData,
    "views_percent"
  );

  const deviceChartOptions = {
    series:
      deviceDistributionData?.length > 0
        ? transformGraphData(
            deviceDistributionData,
            "percentagebrowser_count",
            "Device"
          )?.series
        : [],
    label:
      deviceDistributionData?.length > 0
        ? transformGraphData(
            deviceDistributionData,
            "percentagebrowser_count",
            "Device"
          )?.label
        : [],
  };

  const zonalChartOptions =
    zoneDistributionData && zoneDistributionData?.length > 0
      ? transformHeatMapData(
          zoneDistributionData,
          "name",
          type === "Reads" ? "read_count" : "view_count"
        )
      : [];

  const handleRequestContent = () => {
    redirectTo(`sponsored-content/request-conetnt/${project_id}`);
  };

  const handleDropdownChange = (type = "Views") => {
    setType(type);
  };

  useEffect(() => {
    if (
      sponsoredContentList &&
      Array.isArray(sponsoredContentList) &&
      sponsoredContentList?.length > 0 &&
      !("id" in activeCampaignGroup)
    ) {
      setActiveCampaignGroup(sponsoredContentList[0]);
    }
  }, [sponsoredContentList]);

  const hasViewAccess = checkProjectAccess("show_view", projectDetails);
  const hasReadAccess = checkProjectAccess("show_read", projectDetails);
  const hasHCPAccess = checkProjectAccess("show_hcp", projectDetails);

  const projectToTargets = {
    project_reads_target: summaryAnalytics?.project_reads_target,
    impressions_target: summaryAnalytics?.impressions_target,
    unique_reach_target: summaryAnalytics?.unique_reach_target,
  };

  return (
    <Row>
      <Col md={12}>
        {!isProjectDetailsLoading && (
          <ProjectHeaderTopBar
            projectType="Sponsored Content"
            title="Summary"
            projectStartDate={projectDetails[0]?.project_start_date || ""}
            projectEndDate={projectDetails[0]?.project_end_date || ""}
            isProjectDetailsLoading={isProjectDetailsLoading}
            rightComponent={
              <ProjectFilters
                title="Summary"
                setDateRangePayload={setDateRangePayload}
                isSummaryListLoading={isSummaryListLoading}
                projectStartDate={projectDetails[0]?.project_start_date || ""}
                projectEndDate={projectDetails[0]?.project_end_date || ""}
                dateLabel={dateRangePayload}
                tabs={summaryList}
                activeSummaryTab={activeSummaryTab}
                setActiveSummaryTab={setActiveSummaryTab}
              />
            }
          />
        )}
      </Col>

      <Col md={12}>
        <Card>
          <Card.Body className="border border-success border-2 rounded-3 border-opacity-25 p-0">
            {!isSummaryListLoading &&
            !isSummaryAnalyticsLoading &&
            summaryList?.length > 0 ? (
              <SummeryMatrix
                readCount={summaryAnalytics?.read_count}
                totalCount={summaryAnalytics?.total_count}
                reachCount={summaryAnalytics?.reach_count}
                contentCount={summaryAnalytics?.content_count}
                projectToTargets={projectToTargets}
                hasViewAccess={hasViewAccess}
                hasReadAccess={hasReadAccess}
                hasHCPAccess={hasHCPAccess}
                page="summary"
                title={["Project Details ", " Overview"]}
              />
            ) : (
              <MatrixSkeleton title={["Project Details ", " Overview"]} />
            )}
          </Card.Body>
        </Card>
      </Col>

      {hasReadAccess && (
        <Col md={12} className="mb-4">
          <LineChart
            seriesName="Read (count)"
            chartTitle={"Content Read"}
            graphColor={GRAPH_FILL_COLOR}
            labels={contentReadChartOptions.labels}
            series={contentReadChartOptions.series}
            sponsoredContentList={sponsoredContentList}
            activeCampaignGroup={activeCampaignGroup}
            setActiveCampaignGroup={setActiveCampaignGroup}
            loading={isContentReadLoading}
            needSelect={false}
            hasData={
              contentReadChartOptions?.series &&
              Array.isArray(contentReadChartOptions?.series) &&
              contentReadChartOptions?.series?.length > 0
                ? true
                : false
            }
          />
        </Col>
      )}

      {hasViewAccess && (
        <Col md={12} className="mb-4">
          <LineChart
            seriesName="View (count)"
            chartTitle={"Content Views"}
            graphColor={GRAPH_FILL_COLOR}
            labels={contentViewsChartOptions.labels}
            series={contentViewsChartOptions.series}
            sponsoredContentList={sponsoredContentList}
            activeCampaignGroup={activeCampaignGroup}
            setActiveCampaignGroup={setActiveCampaignGroup}
            loading={isContentGraphLoading}
            needSelect={false}
            hasData={
              contentViewsChartOptions?.series &&
              Array.isArray(contentViewsChartOptions?.series) &&
              contentViewsChartOptions?.series?.length > 0
                ? true
                : false
            }
          />
        </Col>
      )}

      <FilterBy
        handleDropdownChange={handleDropdownChange}
        type={type}
        needHeading={true}
      />

      <Col md={12} lg={6} sm={12} className="d-flex flex-column pe-2 mb-4">
        {!isProjectDetailsLoading &&
        !isStateMapLoading &&
        stateMapData?.length > 0 ? (
          <IndiaMap
            stateData={type === "Reads" ? stateMapReadsData : stateMapViewsData}
            title={"Location"}
            valueSuffix={"%"}
          />
        ) : isStateMapLoading || isProjectDetailsLoading ? (
          <MapAnalyticsSkeleton />
        ) : (
          <CardNotFound title="Location" height={320} />
        )}
      </Col>

      <Col md={12} lg={6}>
        <Row>
          <Col md={12} sm={12} className="mb-4">
            {!isProjectDetailsLoading &&
            !isDeviceDistributionLoading &&
            deviceDistributionData?.length > 0 ? (
              <DonutChart
                series={deviceChartOptions.series}
                labels={deviceChartOptions.label}
                title={"Device Distribution"}
                graphColor={GRAPH_FILL_COLOR}
                height={320}
              />
            ) : isDeviceDistributionLoading || isProjectDetailsLoading ? (
              <SquareChartSkeleton />
            ) : (
              <CardNotFound title="Device Distribution" height={320} />
            )}
          </Col>
          <Col md={12} sm={12} className="mb-4">
            {!isProjectDetailsLoading &&
            !isZonalDistributionLoading &&
            zoneDistributionData?.length > 0 ? (
              <TreeMapChart
                title={"Zonal Distribution"}
                series={zonalChartOptions}
              />
            ) : isZonalDistributionLoading || isProjectDetailsLoading ? (
              <SquareChartSkeleton />
            ) : (
              <CardNotFound title="Zonal Distribution" height={300} />
            )}
          </Col>
        </Row>
      </Col>

      {/* TODO: WHEN REDIRECT UI COMPLETED UN_COMMENT */}
      {/* <Col md={6} sm={12} className="mb-4">
        <ContentActionCard
          heading="Request Content!"
          content={
            "Reach out to the CLIRNET content team to requisition Campaign related content."
          }
          buttonText={"Request Now"}
          icon={"request"}
          pathname={"help-and-support/resources"}
        />
      </Col>
      <Col md={6} sm={12} className="mb-4">
        <ContentActionCard
          heading="Request Campaign"
          content={
            "Configure a Content based Campaign for different Social Media before you send it"
          }
          buttonText={"Create Campaign"}
          icon={"create"}
          pathname={"campaign/create"}
        />
      </Col> */}
      {/* TODO: UPTO THIS ONLY */}

      {/* <Col className="pe-2 mt-0 mt-md-3 mb-4" xs={12} lg={6} sm={6}>
        <CampaignCard
          icon={findIcon("Request Content", "dual-tone", 40)}
          title={"Request Content!"}
          subTitle={
            " Reach out to the CLIRNET content team to requisition Campaign related content."
          }
          buttonText={"Request Now"}
          handleOnClick={handleRequestContent}
        />
      </Col>

      <Col className="mt-0 mt-md-3 mb-4" xs={12} lg={6} sm={6}>
        <CampaignCard
          icon={findIcon("Request Content", "dual-tone", 40)}
          title={"Request Campaign!"}
          subTitle={
            "Configure a content based campaign for different social media before you send it"
          }
          buttonText={"Create Campaign"}
          // handleOnClick={handleRequestNowClick}
        />
      </Col> */}
    </Row>
  );
}

export default Summary;
